import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { POI_CATEGORIES, POI_COLORS, POIS } from "@/data/property";
import SectionHeader from "@/components/SectionHeader";

export default function Neighbourhood() {
  const [cat, setCat] = useState("schools");
  const [activePoi, setActivePoi] = useState(null);

  const pois = POIS.filter((p) => p.cat === cat);

  return (
    <section id="neighbourhood" data-testid="neighbourhood-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Neighbourhood"
          title="Everything Waterloo, close at hand"
          sub="Universities, parks, shopping and transit around Beechwood Place. Shown for orientation — distances on request."
        />

        <div className="mb-8 flex flex-wrap gap-2">
          {POI_CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`poi-tab-${c.id}`}
              onClick={() => {
                setCat(c.id);
                setActivePoi(null);
              }}
              className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                cat === c.id
                  ? "border-zinc-900 bg-zinc-900 text-white dark:border-white dark:bg-white dark:text-black"
                  : "border-zinc-300 text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-white dark:hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div
            data-testid="neighbourhood-map-container"
            className="h-[420px] overflow-hidden rounded-3xl border border-zinc-200 dark:border-zinc-800 lg:h-auto"
          >
            <svg viewBox="0 0 100 100" className="h-full w-full">
              <rect width="100" height="100" className="fill-[#EAE8E3] dark:fill-[#14161A]" />
              <g
                fill="none"
                strokeLinecap="round"
                strokeWidth="1.4"
                className="stroke-white dark:stroke-zinc-700"
              >
                <path d="M0,54 C25,50 45,60 70,50" />
                <path d="M20,0 C24,25 14,48 26,100" />
                <path d="M0,20 C30,24 60,14 100,22" />
                <path d="M50,100 C54,78 44,62 56,40 C64,26 60,12 66,0" />
                <path d="M0,80 C30,76 55,84 80,76" />
              </g>
              <path
                d="M0,88 C20,84 40,92 60,88"
                fill="none"
                strokeWidth="2.4"
                className="stroke-[#B9C9C1] dark:stroke-[#1C2A24]"
              />
              <text x="2" y="97" fontSize="3" className="fill-zinc-500 dark:fill-zinc-400">
                Laurel Creek
              </text>

              {pois.map((p) => {
                const active = activePoi === p.id;
                return (
                  <g
                    key={p.id}
                    data-testid={`poi-dot-${p.id}`}
                    onClick={() => setActivePoi(active ? null : p.id)}
                    className="cursor-pointer"
                  >
                    {active && (
                      <circle cx={p.x} cy={p.y} r="5.4" fill="none" stroke={POI_COLORS[cat]} strokeWidth="0.7" />
                    )}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={active ? 3.2 : 2.4}
                      fill={POI_COLORS[cat]}
                      className="transition-all duration-200"
                    />
                    <text
                      x={p.x + 5}
                      y={p.y + 1.4}
                      fontSize="3"
                      fontWeight={active ? "bold" : "normal"}
                      className="fill-zinc-800 dark:fill-zinc-100"
                    >
                      {p.name}
                    </text>
                  </g>
                );
              })}

              <path d="M46,50 L52,56 L46,62 L40,56 Z" className="fill-zinc-900 dark:fill-white" />
              <text
                x="46"
                y="69"
                fontSize="3.4"
                fontWeight="bold"
                textAnchor="middle"
                className="fill-zinc-900 dark:fill-white"
              >
                461 BEECHWOOD
              </text>
            </svg>
          </div>

          <div className="flex flex-col">
            <div className="flex-1 space-y-3 overflow-y-auto pr-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={cat}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="space-y-3"
                >
                  {pois.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`poi-card-${p.id}`}
                      onClick={() => setActivePoi(activePoi === p.id ? null : p.id)}
                      className={`flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ${
                        activePoi === p.id
                          ? "border-bronze bg-bronze/5"
                          : "border-zinc-200 bg-white hover:border-zinc-400 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-600"
                      }`}
                    >
                      <span
                        className="mt-1 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: POI_COLORS[cat] }}
                      >
                        <MapPin size={16} />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center justify-between gap-3">
                          <span className="font-display text-base font-semibold text-zinc-900 dark:text-white">
                            {p.name}
                          </span>
                          <span className="flex-none text-xs font-semibold uppercase tracking-[0.15em] text-bronze">
                            {p.dist}
                          </span>
                        </span>
                        <span className="mt-1 block text-sm text-zinc-600 dark:text-zinc-400">{p.desc}</span>
                      </span>
                    </button>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
